import { captureException } from '@sentry/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { z } from 'zod';

type SetValue<T> = (value: T | ((prev: T) => T)) => void;

type UseLocalStorageOptions<T> = {
    schema?: z.ZodType<T>;
};

export function useLocalStorage<T>(
    key: string,
    initialValue: T,
    options?: UseLocalStorageOptions<T>,
): [T, SetValue<T>] {
    const getLocalStorageValue = () => {
        try {
            const item = localStorage.getItem(key);
            if (item !== null) {
                const parsed = JSON.parse(item);
                return options?.schema ? options.schema.parse(parsed) : (parsed as T);
            }
        } catch (error) {
            captureException(
                new Error(`Error reading localStorage key "${key}"`, {
                    cause: error,
                }),
            );
        }
        return initialValue;
    };

    const [storedValue, setStoredValue] = useState<T>(getLocalStorageValue);

    useEffect(() => {
        setStoredValue(getLocalStorageValue());
    }, [key]);

    const ref = useRef(storedValue);
    ref.current = storedValue;

    const setValue: SetValue<T> = useCallback(
        (value) => {
            try {
                const valueToStore =
                    typeof value === 'function' ? (value as (prev: T) => T)(ref.current) : value;
                setStoredValue(valueToStore);
                localStorage.setItem(key, JSON.stringify(valueToStore));
            } catch (error) {
                captureException(
                    new Error(`Error wrtiting value "${value}" to localStorage key "${key}"`, {
                        cause: error,
                    }),
                );
            }
        },
        [key],
    );

    return [storedValue, setValue];
}
